import { validateText } from '../mixins/component/validateText';
import { validateNumber } from '../mixins/component/validateNumber';
import { ref, getCurrentInstance, computed, unref } from 'vue';
import { validateCombobox } from '../mixins/component/validateCombobox';
import i18nValidate from '@/i18ns/vi/i18nValidate';

export const ValidateRules = {
  required: 'required',
  moreThan: 'moreThan',
  lessThan: 'lessThan',
  equals: 'equals',
  pattern: 'pattern',
  diffierentZero: 'diffierentZero',
  custom: 'custom',
};

/**
 * Các control muốn validate cần thêm class này vào thẻ html to nhất
 */
export const ControlValidateClass = 'ms-control-validate-class';

export const useValidateControl = ({ props }, controlElement) => {
  /**
   * Thông báo lỗi
   */
  const errorMessage = ref('');

  /**
   * Nếu đã validate 1 lần rồi thì có giá trị là true
   */
  const validated = ref(false);

  /**
   * Instance của component sử dụng composition này
   */
  const { proxy } = getCurrentInstance();

  /**
   * Có validate ko dựa vào số lượng rules
   */
  const isValidate = computed(() => props.rules?.length > 0);

  /**
   * Hàm thực hiện validate
   * @author: BMThang(14/11/2022)
   */
  const validate = async (validateWhenSaving = false) => {
    // Nếu là editor của grid thì sẽ lấy config từ column
    let rules = [];
    if (proxy._isGridEditor) {
      rules = props.col.rules;
    } else {
      rules = props.rules;
    }
    let message = '';
    validated.value = true;
    const ruleCount = rules.length;
    if (Array.isArray(rules)) {
      for (let index = 0; index < ruleCount; index++) {
        const rule = rules[index];
        if (rule) {
          const { name, compareValue, customValidate, isValidateWhenOutFocus } = rule;
          const controlValue = getValueControl();
          // Nếu có custom validate sẽ lấy theo customvalidate
          if (typeof customValidate === 'function') {
            if (
              isValidateWhenOutFocus === true || // Chỉ call customValidate khi cho phép validate khi outfocus
              validateWhenSaving === true // hoặc validate trước khi lưu
            ) {
              message = await customValidate(
                controlValue,
                compareValue,
                getTitleError()
              );
            }
            if (message) {
              break;
            }
          } else {
            message = handlerRule(name, controlValue, compareValue);
            if (message) {
              break;
            }
          }
        }
      }
    }

    errorMessage.value = message;
    
    return {
      message,
      rules,
      isValid: message ? false : true,
      element: { focusError: proxy.focusError, errorElement: unref(controlElement) },
    };
  };

  /**
   * Hàm focus vào ô input khi gặp lỗi.
   * Component có thể tự tạo hàm focusError để MsValidate dùng khi có lỗi
   * @author: BMThang(14/11/2022)
   */
  const focusError = () => {
    const element = unref(controlElement);
    if (!element) {
      return;
    }
    if (typeof element.focus !== 'function') {
      return;
    }
    element.focus();
  };

  /**
   * Xử lý các rule validate trả về thông báo lỗi
   * @param {*} rule tên rule
   * @param {*} controlValue value
   * @param {*} compareValue key để so sánh nếu rule = ValidateRules.pattern
   * @returns msg lỗi nếu có lỗi ngược lại trả về chuỗi rỗng
   */
  const handlerRule = (rule, controlValue, compareValue) => {
    let message = '';
    const titleError = getTitleError();

    switch (rule) {
      case ValidateRules.required:
        if (
          controlValue === undefined ||
          controlValue === null ||
          controlValue === '' ||
          (Array.isArray(controlValue) && controlValue.length <= 0)
        ) {
          message = i18nValidate.messages[rule](titleError);
        }
        break;
      case ValidateRules.moreThan:
        break;
      case ValidateRules.lessThan:
        break;
      case ValidateRules.equals:
        break;
      case ValidateRules.pattern:
        if (!validateText.validateRule(compareValue, controlValue)) {
          message = i18nValidate.messages['email'](titleError);
        }
        break;
      case ValidateRules.diffierentZero:
        if (controlValue === 0) {
          message = i18nValidate.messages['diffierentZero'](titleError);
        }
        break;
      default:
        break;
    }
    return message;
  };

  /**
   * Trả về thông báo lỗi required cho combobox
   * @param {*} controlValue Giá trị của combobox
   * @param {string} ruleName Tên của rule
   * @param {string} titleError Title của control
   * @returns Msg lỗi
   * @author: BMThang(14/11/2022)
   */
  const handleValidateComboboxRequired = (controlValue, ruleName, titleError) => {
    if (
      controlValue === undefined ||
      controlValue === null ||
      controlValue === '' ||
      (Array.isArray(controlValue) && controlValue.length <= 0)
    ) {
      return i18nValidate.messages[ruleName](titleError);
    }
    return "";
  }

  /**
   * Xử lý validate riêng cho combobox
   * @author: BMThang(14/11/2022)
   */
  const handlerValidateCombobox = () => {
    const { rules } = props;
    let message = '';
    const controlValue = getValueControl();
    const titleError = getTitleError();
    validated.value = true;
    if (Array.isArray(rules) && rules.length > 0) {
      for (let index = 0; index < rules.length; index++) {
        const rule = rules[index];
        if (rule) {
          const { name, compareValue, customValidate } = rule;

          // Nếu có customValidate thì thực hiện hàm này
          if (typeof customValidate === 'function') {
            message = customValidate(
              controlValue,
              compareValue,
              titleError,
            );
          }
          else {
            // Nếu là required
            if (name === ValidateRules.required) {
              message = handleValidateComboboxRequired(controlValue, name, titleError);
            }
          }
        }
        if (message) {
          break;
        }
      }
    }

    errorMessage.value = message;

    return {
      message,
      rules,
      isValid: message ? false : true,
      element: { focusError: proxy.focusError, errorElement: unref(controlElement) },
    };
  };

  /**
   * Xóa msg lỗi
   */
  const clearValidate = () => {
    errorMessage.value = '';
  };

  /**
   * Lấy value của control (Các control override hàm getValue)
   * @returns
   */
  const getValueControl = () => {
    if (typeof proxy.getValue === 'function') {
      return proxy.getValue();
    }
    return null;
  };

  /**
   * Lấy title của error
   * @returns
   */
  const getTitleError = () => {
    const { title, name } = props;
    if (proxy._isGridEditor) {
      return props.col.caption;
    }
    // Nếu có name thì return name luôn
    if (name) {
      return name;
    }
    return title || getValidateName();
  };

  /**
   * Nếu control không khai báo name để hiển thị thông tin lỗi validate -> up parent find label
   */
  const getValidateName = () => {
    let fn = function (el, c) {
      if (!el || c > 10) {
        return null;
      }

      let labelEl = el.querySelector('label');
      if (labelEl) {
        return (labelEl.innerText || '').replace('*', '');
      }

      return fn(el.parentNode, c + 1);
    };

    return fn(proxy.$el, 0);
  };
  return {
    errorMessage,
    isValidate,
    validated,
    validate,
    handlerValidateCombobox,
    getTitleError,
    clearValidate,
    focusError,
  };
};